import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowUp,
  CornerDownRight,
  CornerUpLeft,
  ExternalLink,
  Link2,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import { CatalogHead } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { menuCreate, menuDelete, menuUpdate, menusList } from '@/shared/api/generated/menus/menus';
import type { MenuBody, MenuItemBody } from '@/shared/api/generated/model';
import { recordsGet } from '@/shared/api/generated/records/records';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog, Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import { MediaPicker } from '../RecordPage/MediaPicker';
import { ReferenceInput } from '../RecordPage/ReferenceInput';
import { slugOf } from '../SchemaPage/keyify';
import {
  MAX_DEPTH,
  append,
  canIndent,
  indent,
  itemAt,
  move,
  outdent,
  remove,
  update,
  type Path,
} from './menuTree';
import styles from './NavigationPage.module.scss';

const menusKey = (siteId: string, env: EnvKey) => ['menus', siteId, env] as const;

/**
 * «Навигация» — меню сайта: шапка, подвал. Пункты деревом: на страницу,
 * запись или адрес; вложенные — выпадающее меню (до трёх уровней). Правка
 * сохраняется сразу и сразу видна сайту.
 */
export function NavigationPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env/navigation' });
  const scope = useSiteScope();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string>();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<{ path: Path | undefined; parent: Path }>();
  const [deleting, setDeleting] = useState(false);

  const shaper = scope?.can('site.shape') ?? false;
  const editor = scope?.can('content.publish') ?? false;

  const menus = useQuery({
    queryKey: menusKey(siteId, env),
    queryFn: async () => {
      const response = await menusList(siteId, kinds[env]);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос меню');
      }

      return response.data.items ?? [];
    },
  });

  const list = menus.data ?? [];
  const menu = list.find((item) => item.id === selected) ?? list[0];
  const items = menu?.items ?? [];

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: menusKey(siteId, env) });
  };

  const save = (next: MenuItemBody[]) => {
    if (menu === undefined) {
      return;
    }

    // Сразу показываем новое дерево, сервер догоняет.
    queryClient.setQueryData<MenuBody[]>(menusKey(siteId, env), (old) =>
      old?.map((item) => (item.id === menu.id ? { ...item, items: next } : item)),
    );

    menuUpdate(menu.id, { items: next }, { 'If-Match': menu.etag })
      .then((response) => {
        const saved = response.data as MenuBody;

        queryClient.setQueryData<MenuBody[]>(menusKey(siteId, env), (old) =>
          old?.map((item) => (item.id === saved.id ? saved : item)),
        );
      })
      .catch((cause: unknown) => {
        toast.error(
          cause instanceof ApiError && cause.status === 412
            ? 'Меню успело измениться — показано свежее'
            : cause instanceof ApiError
              ? cause.detail
              : 'Не сохранилось',
        );
        refresh();
      });
  };

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title="Навигация"
        subtitle="Меню сайта: шапка, подвал. Пункт ведёт на страницу, запись или адрес; вложенные пункты — выпадающее меню. Сохраняется сразу."
        actions={
          shaper && (
            <Button
              variant="outline"
              tone="neutral"
              iconLeft={<Plus size={15} />}
              onClick={() => {
                setCreating(true);
              }}
            >
              Новое меню
            </Button>
          )
        }
      />

      {menus.isPending ? null : list.length === 0 ? (
        <p className={catalog['empty']}>
          Меню пока нет. Заведите «Шапку» с ключом header — сайт получит её пункты готовыми
          ссылками.
        </p>
      ) : (
        <>
          <div className={styles['tabs']} role="tablist" aria-label="Меню">
            {list.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={item.id === menu?.id}
                className={styles['tab']}
                onClick={() => {
                  setSelected(item.id);
                }}
              >
                {item.name}
                <span className={catalog['mono']}>{item.key}</span>
              </button>
            ))}
          </div>

          {menu !== undefined && (
            <div className={styles['editor']}>
              {items.length === 0 ? (
                <p className={catalog['empty']}>В меню пока нет пунктов.</p>
              ) : (
                <ul className={styles['tree']}>
                  {items.map((item, index) => (
                    <Row
                      key={item.id ?? index}
                      item={item}
                      path={[index]}
                      items={items}
                      editable={editor}
                      onChange={save}
                      onEdit={(path) => {
                        setEditing({ path, parent: path.slice(0, -1) });
                      }}
                      onAddChild={(path) => {
                        setEditing({ path: undefined, parent: path });
                      }}
                    />
                  ))}
                </ul>
              )}

              <div className={styles['actions']}>
                {editor && (
                  <Button
                    iconLeft={<Plus size={15} />}
                    onClick={() => {
                      setEditing({ path: undefined, parent: [] });
                    }}
                  >
                    Пункт
                  </Button>
                )}
                {shaper && (
                  <Button
                    variant="ghost"
                    tone="danger"
                    iconLeft={<Trash2 size={15} />}
                    onClick={() => {
                      setDeleting(true);
                    }}
                  >
                    Удалить меню «{menu.name}»
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={editing !== undefined} onOpenChange={(open) => !open && setEditing(undefined)}>
        {editing !== undefined && (
          <ItemForm
            item={editing.path === undefined ? undefined : itemAt(items, editing.path)}
            onSave={(item) => {
              save(
                editing.path === undefined
                  ? append(items, editing.parent, item)
                  : update(items, editing.path, item),
              );
              setEditing(undefined);
            }}
          />
        )}
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        {creating && (
          <NewMenuForm
            onCreate={(key, name) => {
              menuCreate(siteId, kinds[env], { key, name })
                .then((response) => {
                  setSelected((response.data as MenuBody).id);
                  setCreating(false);
                  refresh();
                })
                .catch((cause: unknown) => {
                  toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось завести меню');
                });
            }}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={`Удалить меню «${menu?.name ?? ''}»?`}
        description={`Сайт перестанет получать меню ${menu?.key ?? ''}. Вернуть его можно только заново.`}
        confirmLabel="Удалить"
        onConfirm={async () => {
          if (menu === undefined) {
            return;
          }

          try {
            await menuDelete(menu.id);
            setSelected(undefined);
            refresh();
          } catch (cause) {
            toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось удалить');
          }
        }}
      />
    </div>
  );
}

function Row({
  item,
  path,
  items,
  editable,
  onChange,
  onEdit,
  onAddChild,
}: {
  item: MenuItemBody;
  path: Path;
  items: MenuItemBody[];
  editable: boolean;
  onChange: (items: MenuItemBody[]) => void;
  onEdit: (path: Path) => void;
  onAddChild: (path: Path) => void;
}) {
  const index = path.at(-1) ?? 0;
  const siblings = path.length === 1 ? items : (itemAt(items, path.slice(0, -1))?.children ?? []);
  const children = item.children ?? [];

  return (
    <li>
      <div className={styles['row']}>
        <span className={styles['row__label']}>{item.label}</span>
        <span className={styles['row__target']}>
          {item.kind === 'record' ? (
            <RecordTarget id={item.record_id ?? ''} />
          ) : (
            <>
              <Link2 size={13} />
              {item.url}
            </>
          )}
          {item.new_tab === true && <ExternalLink size={13} aria-label="в новой вкладке" />}
        </span>
        {editable && (
          <span className={styles['row__actions']}>
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label="Выше"
              disabled={index === 0}
              onClick={() => {
                onChange(move(items, path, -1));
              }}
            >
              <ArrowUp size={14} />
            </Button>
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label="Ниже"
              disabled={index === siblings.length - 1}
              onClick={() => {
                onChange(move(items, path, 1));
              }}
            >
              <ArrowDown size={14} />
            </Button>
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label="Вложить в пункт выше"
              disabled={!canIndent(items, path)}
              onClick={() => {
                onChange(indent(items, path));
              }}
            >
              <CornerDownRight size={14} />
            </Button>
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label="На уровень выше"
              disabled={path.length < 2}
              onClick={() => {
                onChange(outdent(items, path));
              }}
            >
              <CornerUpLeft size={14} />
            </Button>
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label="Вложенный пункт"
              disabled={path.length >= MAX_DEPTH}
              onClick={() => {
                onAddChild(path);
              }}
            >
              <Plus size={14} />
            </Button>
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label="Изменить"
              onClick={() => {
                onEdit(path);
              }}
            >
              <Pencil size={14} />
            </Button>
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="danger"
              aria-label="Удалить пункт"
              onClick={() => {
                onChange(remove(items, path));
              }}
            >
              <Trash2 size={14} />
            </Button>
          </span>
        )}
      </div>
      {children.length > 0 && (
        <ul className={styles['tree']}>
          {children.map((child, childIndex) => (
            <Row
              key={child.id ?? childIndex}
              item={child}
              path={[...path, childIndex]}
              items={items}
              editable={editable}
              onChange={onChange}
              onEdit={onEdit}
              onAddChild={onAddChild}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function RecordTarget({ id }: { id: string }) {
  const record = useQuery({
    queryKey: ['record', id],
    enabled: id !== '',
    queryFn: async () => {
      const response = await recordsGet(id);

      return response.status === 200 ? response.data : null;
    },
  });

  return (
    <>
      <Link2 size={13} />
      {record.isPending
        ? '…'
        : record.data == null
          ? 'запись не найдена'
          : record.data.title || 'без названия'}
    </>
  );
}

function ItemForm({
  item,
  onSave,
}: {
  item: MenuItemBody | undefined;
  onSave: (item: MenuItemBody) => void;
}) {
  const [label, setLabel] = useState(item?.label ?? '');
  const [kind, setKind] = useState<MenuItemBody['kind']>(item?.kind ?? 'record');
  const [recordID, setRecordID] = useState(item?.record_id ?? '');
  const [url, setUrl] = useState(item?.url ?? '');
  const [newTab, setNewTab] = useState(item?.new_tab ?? false);
  const [image, setImage] = useState(item?.image ?? '');
  const [imageName, setImageName] = useState('');
  const [note, setNote] = useState(item?.note ?? '');
  const [picking, setPicking] = useState(false);
  const params = useParams({ strict: false });
  const siteID = typeof params.siteId === 'string' ? params.siteId : '';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSave({
      ...(item?.id === undefined ? {} : { id: item.id }),
      label: label.trim(),
      kind,
      ...(kind === 'record' ? { record_id: recordID } : { url: url.trim() }),
      new_tab: newTab,
      ...(image === '' ? {} : { image }),
      ...(note.trim() === '' ? {} : { note: note.trim() }),
      ...(item?.children ? { children: item.children } : {}),
    });
  };

  const ready = label.trim() !== '' && (kind === 'record' ? recordID !== '' : url.trim() !== '');

  return (
    <DialogContent
      title={item === undefined ? 'Новый пункт' : 'Пункт меню'}
      size="sm"
      footer={
        <Button type="submit" form="menu-item" disabled={!ready}>
          {item === undefined ? 'Добавить' : 'Сохранить'}
        </Button>
      }
    >
      <form id="menu-item" className={styles['form']} onSubmit={submit} noValidate>
        <Field label="Подпись" required>
          <Input
            autoFocus
            value={label}
            placeholder="Кейсы"
            onChange={(event) => {
              setLabel(event.target.value);
            }}
          />
        </Field>
        <Field label="Куда ведёт">
          <Segmented
            value={kind}
            onValueChange={(next) => {
              setKind(next as MenuItemBody['kind']);
            }}
            aria-label="Куда ведёт"
            options={[
              { value: 'record', label: 'Страница или запись' },
              { value: 'url', label: 'Адрес' },
            ]}
          />
        </Field>
        {kind === 'record' ? (
          <Field label="Запись" hint="Адрес сайт получит сам — по шаблону адреса её модели.">
            <ReferenceInput
              value={recordID}
              multiple={false}
              collection=""
              onChange={(value) => {
                setRecordID(typeof value === 'string' ? value : '');
              }}
            />
          </Field>
        ) : (
          <Field label="Адрес" hint="«/contacts», «https://t.me/…», «mailto:…», «#форма».">
            <Input
              mono
              value={url}
              placeholder="/contacts"
              onChange={(event) => {
                setUrl(event.target.value);
              }}
            />
          </Field>
        )}
        <Switch checked={newTab} onCheckedChange={setNewTab}>
          Открывать в новой вкладке
        </Switch>
        <Field label="Подпись под пунктом" hint="Для мега-меню: «Новая коллекция». Необязательно.">
          <Input
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </Field>
        <Field label="Картинка" hint="Для мега-меню — баннер в выпадающем меню. Необязательно.">
          <div className={styles['actions']}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setPicking(true);
              }}
            >
              {image === '' ? 'Выбрать картинку' : 'Заменить'}
            </Button>
            {image !== '' && (
              <>
                <span>{imageName || 'выбрана'}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setImage('');
                    setImageName('');
                  }}
                >
                  Убрать
                </Button>
              </>
            )}
          </div>
        </Field>
        <MediaPicker
          open={picking}
          onOpenChange={setPicking}
          siteID={siteID}
          kind="image"
          chosenID={image === '' ? undefined : image}
          onChoose={(asset) => {
            setImage(asset.id);
            setImageName(asset.name);
            setPicking(false);
          }}
        />
      </form>
    </DialogContent>
  );
}

function NewMenuForm({ onCreate }: { onCreate: (key: string, name: string) => void }) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [touched, setTouched] = useState(false);

  return (
    <DialogContent
      title="Новое меню"
      size="sm"
      footer={
        <Button type="submit" form="new-menu" disabled={name.trim() === '' || key === ''}>
          Завести
        </Button>
      }
    >
      <form
        id="new-menu"
        className={styles['form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onCreate(key, name.trim());
        }}
      >
        <Field label="Название" required>
          <Input
            autoFocus
            value={name}
            placeholder="Шапка"
            onChange={(event) => {
              setName(event.target.value);

              if (!touched) {
                setKey(slugOf(event.target.value).replace(/-/g, '_'));
              }
            }}
          />
        </Field>
        <Field label="Ключ" required hint="По нему сайт находит меню: header, footer.">
          <Input
            mono
            value={key}
            placeholder="header"
            onChange={(event) => {
              setTouched(true);
              setKey(event.target.value);
            }}
          />
        </Field>
      </form>
    </DialogContent>
  );
}
